'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { DOCS_NAV } from './nav';

function NavList({ pathname, query, onNavigate }: { pathname: string; query: string; onNavigate?: () => void }) {
  const q = query.trim().toLowerCase();

  const groups = useMemo(() => {
    if (!q) return DOCS_NAV;
    return DOCS_NAV.map((group) => ({
      ...group,
      items: group.items.filter(
        (item) =>
          item.title.toLowerCase().includes(q) || (item.keywords?.toLowerCase().includes(q) ?? false),
      ),
    })).filter((group) => group.items.length > 0);
  }, [q]);

  if (groups.length === 0) {
    return <p className="px-3 py-2 text-xs text-neutral-600">No matching pages</p>;
  }

  return (
    <nav aria-label="Documentation" className="space-y-6">
      {groups.map((group) => (
        <div key={group.title}>
          <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
            {group.title}
          </p>
          <ul className="space-y-0.5">
            {group.items.map((item) => {
              const active = pathname === item.href;
              return (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    onClick={onNavigate}
                    aria-current={active ? 'page' : undefined}
                    className={`block border-l-2 px-3 py-1.5 text-sm transition-colors ${
                      active
                        ? 'border-rose-500 bg-rose-500/5 text-rose-400'
                        : 'border-transparent text-neutral-400 hover:border-neutral-600 hover:text-white'
                    }`}
                  >
                    {item.title}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

export default function DocsSidebar({ onNavigate }: { onNavigate?: () => void }) {
  const pathname = usePathname();
  const [query, setQuery] = useState('');

  return (
    <div>
      <div className="relative mb-4">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search docs..."
          aria-label="Search documentation"
          className="w-full border border-neutral-800/60 bg-neutral-950/60 py-2 pl-9 pr-3 text-sm text-white placeholder:text-neutral-600 outline-none transition-colors focus:border-rose-500/40"
        />
      </div>
      <NavList pathname={pathname} query={query} onNavigate={onNavigate} />
    </div>
  );
}
